<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDateLocale } from 'dashboard/composables/useTimeAgo';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import EcommerceAPI from 'dashboard/api/ecommerce';
import TrackingEventDetailModal from './components/TrackingEventDetailModal.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  listClass: {
    type: String,
    default: 'px-6 py-3 space-y-2',
  },
});

const { t } = useI18n();
const { formatDate: formatWithLocale } = useDateLocale();

const detailModalRef = ref(null);

const events = ref([]);
const loading = ref(true);
const error = ref('');
const page = ref(1);
const totalCount = ref(0);

const hasMore = computed(() => events.value.length < totalCount.value);

const EVENT_ICONS = {
  page_viewed: 'i-lucide-globe',
  product_viewed: 'i-lucide-package',
  collection_viewed: 'i-lucide-layout-grid',
  search_submitted: 'i-lucide-search',
  cart_viewed: 'i-lucide-shopping-cart',
  product_added_to_cart: 'i-lucide-shopping-cart',
  product_removed_from_cart: 'i-lucide-shopping-cart',
  checkout_started: 'i-lucide-credit-card',
  customer_identified: 'i-lucide-user-check',
  order_completed: 'i-lucide-shopping-bag',
};

const fetchEvents = async (nextPage = 1, append = false) => {
  try {
    loading.value = true;
    const response = await EcommerceAPI.getContactTrackingEvents(
      props.contactId,
      nextPage
    );
    totalCount.value = response.data.meta?.total_count || 0;
    events.value = append
      ? [...events.value, ...response.data.tracking_events]
      : response.data.tracking_events;
    page.value = nextPage;
  } catch (e) {
    if (!append) events.value = [];
    error.value = e.response?.data?.error || 'Error loading browsing history';
  } finally {
    loading.value = false;
  }
};

const loadMore = () => fetchEvents(page.value + 1, true);

watch(
  () => props.contactId,
  () => fetchEvents(1),
  { immediate: true }
);

const formatDate = dateString =>
  formatWithLocale(dateString, 'd MMM yyyy, HH:mm');

const eventIcon = type => EVENT_ICONS[type] || 'i-lucide-activity';

const eventLabel = event => {
  const p = event.payload || {};
  const name = p.product?.title || p.collection?.title || '';
  const query = p.search_query || '';
  switch (event.event_type) {
    case 'product_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_VIEWED', { name });
    case 'collection_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.COLLECTION_VIEWED', {
        name,
      });
    case 'search_submitted':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.SEARCH_SUBMITTED', {
        query,
      });
    case 'cart_viewed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CART_VIEWED');
    case 'product_added_to_cart':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_ADDED_TO_CART', {
        name,
      });
    case 'product_removed_from_cart':
      return t(
        'CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PRODUCT_REMOVED_FROM_CART',
        { name }
      );
    case 'checkout_started':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CHECKOUT_STARTED');
    case 'order_completed':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.ORDER_COMPLETED');
    case 'customer_identified':
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.CUSTOMER_IDENTIFIED');
    default:
      return t('CONTACTS_LAYOUT.ACTIVITIES.TRACKING.PAGE_VIEWED');
  }
};
</script>

<template>
  <div
    v-if="loading"
    class="flex items-center justify-center py-10 text-n-slate-11"
  >
    <Spinner />
  </div>
  <div v-else-if="error" class="px-6 py-10 text-sm text-center text-n-ruby-11">
    {{ error }}
  </div>
  <p
    v-else-if="!events.length"
    class="px-6 py-10 text-sm leading-6 text-center text-n-slate-11"
  >
    {{ t('CONTACTS_LAYOUT.SIDEBAR.BROWSING_TIMELINE.EMPTY_STATE') }}
  </p>
  <div v-else :class="props.listClass">
    <div
      v-for="event in events"
      :key="event.id"
      class="flex items-start gap-3 rounded-lg border border-n-weak px-3 py-2.5"
    >
      <div
        class="flex items-center justify-center rounded-full size-6 bg-n-alpha-2 shrink-0 mt-0.5"
      >
        <i
          :class="eventIcon(event.event_type)"
          class="size-3.5 text-n-slate-11"
        />
      </div>
      <div class="flex-1 min-w-0">
        <p class="text-sm text-n-slate-12">
          {{ eventLabel(event) }}
        </p>
        <a
          v-if="event.event_type === 'page_viewed' && event.payload?.url"
          :href="event.payload.url"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-n-blue-10 truncate block hover:underline"
        >
          {{ event.payload.url }}
        </a>
        <p class="text-xs text-n-slate-10 mt-0.5">
          {{ formatDate(event.occurred_at) }}
        </p>
      </div>
      <Button
        icon="i-lucide-info"
        size="xs"
        variant="ghost"
        color="slate"
        class="shrink-0 mt-0.5"
        @click="detailModalRef.open(event)"
      />
    </div>
    <Button
      v-if="hasMore"
      variant="outline"
      color="slate"
      size="sm"
      class="self-center w-full mt-1"
      :is-loading="loading"
      :label="t('CONTACTS_LAYOUT.ACTIVITIES.LOAD_MORE')"
      @click="loadMore"
    />
  </div>

  <TrackingEventDetailModal ref="detailModalRef" />
</template>
